import React from "react";

import { FocusableTooltip } from "../../../../../../components/FocusableTooltip";
import { PRE_TAX_IRR_TOOLTIP } from "../../../../../../services/valuation";
import {
  DateFormat,
  durationOf,
  formatCurrency,
  formatDate,
} from "../../../../../../utils";
import { Disclosure, DisclosureSummary } from "../styles";
import { Meta, MetaGrid, MetaLabel, MetaValue } from "./styles";
import { AccountDetailsProps } from "./types";

export const AccountDetails: React.FC<AccountDetailsProps> = ({
  saving,
  counts,
  hasTransactions,
  isOpenByDefault,
}) => {
  const hasIrr = hasTransactions && Number.isFinite(saving.irr);
  let irrText = "—";
  let irrColor: string | undefined;
  if (hasIrr) {
    const sign = saving.irr >= 0 ? "+" : "";
    irrText = `${sign}${saving.irr.toFixed(1)}%`;
    irrColor = saving.irr >= 0 ? "var(--color-success)" : "var(--color-error)";
  }
  const irrValue = (
    <MetaValue $color={irrColor} data-testid="saving-irr">
      {irrText}
    </MetaValue>
  );
  const irrCell = hasIrr ? (
    <FocusableTooltip title={PRE_TAX_IRR_TOOLTIP}>{irrValue}</FocusableTooltip>
  ) : (
    irrValue
  );

  const entriesText = hasTransactions
    ? `${counts.deposits} in · ${counts.withdrawals} out · ${counts.interest} interest`
    : "None yet";
  const deposited = hasTransactions ? saving.investedValue : 0;
  const withdrawn = hasTransactions ? saving.withdrawnValue : 0;

  return (
    <Disclosure open={isOpenByDefault}>
      <DisclosureSummary>Account details</DisclosureSummary>
      <MetaGrid>
        <Meta>
          <MetaLabel>Institution</MetaLabel>
          <MetaValue data-testid="saving-institution">
            {saving.institution || "—"}
          </MetaValue>
        </Meta>
        <Meta>
          <MetaLabel>Start date</MetaLabel>
          <MetaValue>
            {formatDate(saving.startDate, DateFormat.DMY_SPACE)}
          </MetaValue>
        </Meta>
        <Meta>
          <MetaLabel>Duration</MetaLabel>
          <MetaValue>{durationOf(saving)}</MetaValue>
        </Meta>
        <Meta>
          <MetaLabel>IRR · pre-tax</MetaLabel>
          {irrCell}
        </Meta>
        <Meta $isWide>
          <MetaLabel>Entries</MetaLabel>
          <MetaValue data-testid="saving-entries">{entriesText}</MetaValue>
        </Meta>
        <Meta>
          <MetaLabel>Deposited</MetaLabel>
          <MetaValue data-testid="saving-deposited">
            {formatCurrency(deposited)}
          </MetaValue>
        </Meta>
        <Meta>
          <MetaLabel>Withdrawn</MetaLabel>
          <MetaValue data-testid="saving-withdrawn">
            {formatCurrency(withdrawn)}
          </MetaValue>
        </Meta>
      </MetaGrid>
    </Disclosure>
  );
};
